import React, { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import "./MediaLibrary.css";

const TYPE_LABELS = {
  all: "All",
  image: "Images",
  video: "Videos",
  audio: "Audio",
  model: "3D"
};

function guessTypeFromUrl(url) {
  const lower = (url || "").toLowerCase();
  if (lower.endsWith(".mp4") || lower.endsWith(".mov") || lower.endsWith(".webm")) return "video";
  if (lower.endsWith(".wav") || lower.endsWith(".mp3") || lower.endsWith(".ogg")) return "audio";
  if (lower.endsWith(".glb") || lower.endsWith(".gltf") || lower.endsWith(".fbx") || lower.endsWith(".obj") || lower.endsWith(".usdz")) {
    return "model";
  }
  return "image";
}

function parseDate(value) {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

import { buildDownloadName, buildDownloadUrl, downloadFile } from "../utils/download";

export default function MediaLibrary() {
  const storedState = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem("contentos_media_state");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);
  const pickerParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const isPicker = pickerParams.get("picker") === "1";
  const pickerTarget = pickerParams.get("target") || "";
  const pickerMulti = pickerParams.get("multi") !== "0";
  const pickerType = pickerParams.get("type") || "all";
  const pickerReturn = pickerParams.get("return") || "/";
  const isArchivedRoute = window.location.pathname.startsWith("/media/archived");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const storedType = storedState?.typeFilter;
  const storedProject = storedState?.projectFilter;
  const storedSort = storedState?.sortOrder;
  const storedSource = storedState?.sourceFilter;
  const storedTab = storedState?.sourceTab;
  const initialType = TYPE_LABELS[pickerType]
    ? pickerType
    : TYPE_LABELS[storedType]
      ? storedType
      : "all";
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [projectFilter, setProjectFilter] = useState(storedProject || "all");
  const [sortOrder, setSortOrder] = useState(storedSort || "newest");
  const [sourceFilter, setSourceFilter] = useState(storedSource || "all");
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [sourceTab, setSourceTab] = useState(
    storedTab === "public" || (isPicker && storedTab === "uploads") ? storedTab : "mine"
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailTitle, setDetailTitle] = useState("");
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailEditTitle, setDetailEditTitle] = useState(false);
  const [detailProjectId, setDetailProjectId] = useState("");
  const [detailProjectName, setDetailProjectName] = useState("");
  const [detailEditProject, setDetailEditProject] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteItemOpen, setDeleteItemOpen] = useState(false);
  const [deleteItemLoading, setDeleteItemLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [archivedProjectId, setArchivedProjectId] = useState("");
  const [archivedActionLoading, setArchivedActionLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRotate, setEditRotate] = useState(0);
  const [editCropMode, setEditCropMode] = useState(false);
  const [editCropApplied, setEditCropApplied] = useState(false);
  const [editCropRect, setEditCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [editImageMeta, setEditImageMeta] = useState({ naturalW: 0, naturalH: 0, displayW: 0, displayH: 0 });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("jpeg");
  const [exportSize, setExportSize] = useState("original");
  const [exportLoading, setExportLoading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [slideshowSelected, setSlideshowSelected] = useState([]);
  const [slideshowLoading, setSlideshowLoading] = useState(false);
  const [slideshowMsg, setSlideshowMsg] = useState("");
  const cropDragRef = React.useRef(null);
  const editImageRef = React.useRef(null);
  const selectedUrls = useMemo(
    () => selectedEntries.map((entry) => entry.url).filter(Boolean),
    [selectedEntries]
  );

  useEffect(() => {
    if (sourceTab === "uploads" && projectFilter !== "all") {
      setProjectFilter("all");
    }
    const payload = {
      sourceTab,
      typeFilter,
      projectFilter,
      sortOrder,
      sourceFilter
    };
    try {
      window.sessionStorage.setItem("contentos_media_state", JSON.stringify(payload));
    } catch {
      // Ignore storage errors (private mode, quota).
    }
  }, [projectFilter, sortOrder, sourceFilter, sourceTab, typeFilter]);

  useEffect(() => {
    if (!isArchivedRoute) return;
    setSourceTab("mine");
    setProjectFilter("all");
  }, [isArchivedRoute]);

  useEffect(() => {
    let isMounted = true;
    async function loadProjects() {
      try {
        const res = await fetch("/api/fast/projects");
        const data = await res.json();
        if (!res.ok) throw new Error("Erreur chargement projets.");
        if (isMounted) setProjectOptions(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (isMounted) setProjectOptions([]);
      }
    }
    loadProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isArchivedRoute) return () => { };
    let isMounted = true;
    async function loadArchivedProjects() {
      try {
        const res = await fetch("/api/fast/projects?status=archived");
        const data = await res.json();
        if (!res.ok) throw new Error("Erreur chargement projets archives.");
        const items = Array.isArray(data.items) ? data.items : [];
        if (!isMounted) return;
        setArchivedProjects(items);
        if (items.length === 0) {
          setArchivedProjectId("");
        } else if (!archivedProjectId) {
          setArchivedProjectId(items[0].project_id || "");
        }
      } catch {
        if (isMounted) setArchivedProjects([]);
      }
    }
    loadArchivedProjects();
    return () => {
      isMounted = false;
    };
  }, [isArchivedRoute]);

  useEffect(() => {
    if (isArchivedRoute) return () => { };
    let isMounted = true;
    async function loadMedia() {
      setLoading(true);
      setErrorMsg("");
      try {
        const endpoint =
          sourceTab === "public"
            ? "/api/fast/media/public/assets"
            : sourceTab === "uploads"
              ? "/api/fast/media/uploads"
              : "/api/fast/media/assets";
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error("Erreur lors du chargement des medias.");
        const data = await res.json();
        const rawItems = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.images)
            ? data.images.map((url) => ({ url }))
            : [];
        const normalized = rawItems
          .filter((entry) => entry?.url)
          .map((entry) => ({
            id: entry.media_item_id || entry.id || entry.url,
            mediaItemId: entry.media_item_id || "",
            mediaVersionId: entry.version_id || entry.current_version_id || "",
            assetId: entry.asset_id || entry.id || "",
            url: entry.url,
            thumbnailUrl: entry.thumbnail_url || "",
            type: entry.type || guessTypeFromUrl(entry.url),
            source: entry.source || "",
            projectId: entry.project_id || "",
            projectName: entry.project_name || "Sans projet",
            title: entry.title || "",
            createdAt: entry.created_at || "",
            updatedAt: entry.updated_at || ""
          }));
        if (isMounted) setItems(normalized);
      } catch (err) {
        if (isMounted) setErrorMsg(err.message || "Erreur inconnue.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, [sourceTab]);

  useEffect(() => {
    if (!isArchivedRoute) return () => { };
    let isMounted = true;
    async function loadArchivedMedia() {
      setLoading(true);
      setErrorMsg("");
      if (!archivedProjectId) {
        setItems([]);
        setLoading(false);
        return;
      }
      try {
        const endpoint = `/api/fast/media/archived?project_id=${encodeURIComponent(archivedProjectId)}`;
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error("Erreur lors du chargement des medias archives.");
        const data = await res.json();
        const rawItems = Array.isArray(data.items) ? data.items : [];
        const normalized = rawItems
          .filter((entry) => entry?.url)
          .map((entry) => ({
            id: entry.media_item_id || entry.id || entry.url,
            mediaItemId: entry.media_item_id || "",
            mediaVersionId: entry.version_id || entry.current_version_id || "",
            assetId: entry.asset_id || entry.id || "",
            url: entry.url,
            thumbnailUrl: entry.thumbnail_url || "",
            type: entry.type || guessTypeFromUrl(entry.url),
            source: entry.source || "",
            projectId: entry.project_id || "",
            projectName: entry.project_name || "Sans projet",
            title: entry.title || "",
            createdAt: entry.created_at || "",
            updatedAt: entry.updated_at || ""
          }));
        if (isMounted) setItems(normalized);
      } catch (err) {
        if (isMounted) setErrorMsg(err.message || "Erreur inconnue.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadArchivedMedia();
    return () => {
      isMounted = false;
    };
  }, [archivedProjectId, isArchivedRoute]);

  useEffect(() => {
    if (isPicker) return;
    const params = new URLSearchParams(window.location.search);
    const mediaItemId = params.get("media_item_id") || "";
    const assetId = params.get("asset_id") || "";
    const detailId = mediaItemId || assetId;
    if (!detailId) return;
    let isMounted = true;
    async function openDetailById() {
      setDetailItem({ mediaItemId, assetId, url: "", type: "", projectName: "", title: "" });
      setDetailData(null);
      setDetailError("");
      setDetailTitle("");
      setDetailEditTitle(false);
      setDetailEditProject(false);
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const query = mediaItemId
          ? `media_item_id=${encodeURIComponent(mediaItemId)}`
          : `asset_id=${encodeURIComponent(assetId)}`;
        const res = await fetch(`/api/fast/media/detail?${query}`);
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || "Erreur detail media.");
        if (!isMounted) return;
        const item = data.item_view || data.item || {};
        setDetailItem({
          mediaItemId: data.item?.media_item_id || mediaItemId || "",
          assetId: item.id || assetId || "",
          url: item.url || "",
          type: item.type || guessTypeFromUrl(item.url || ""),
          projectName: item.project_name || "",
          title: item.title || ""
        });
        setDetailData(data);
        setDetailTitle(item.title || "");
        setDetailProjectId(item.project_id || data.item?.project_id || "");
        setDetailProjectName(item.project_name || "");
        const initialVersionId =
          data?.item_view?.version_id ||
          data?.item?.current_version_id ||
          data?.versions?.[0]?.version?.media_version_id ||
          "";
        setSelectedVersionId(initialVersionId);
      } catch (err) {
        if (isMounted) setDetailError(err.message || "Erreur detail media.");
      } finally {
        if (isMounted) setDetailLoading(false);
      }
    }
    openDetailById();
    params.delete("asset_id");
    const base = window.location.pathname;
    const query = params.toString();
    window.history.replaceState({}, "", query ? `${base}?${query}` : base);
    return () => {
      isMounted = false;
    };
  }, [isPicker]);

  useEffect(() => {
    if (!detailData?.versions?.length) return;
    const exists = detailData.versions.some(
      (entry) => entry?.version?.media_version_id === selectedVersionId
    );
    if (exists) return;
    const fallbackId =
      detailData?.item_view?.version_id ||
      detailData?.item?.current_version_id ||
      detailData?.versions?.[0]?.version?.media_version_id ||
      "";
    setSelectedVersionId(fallbackId);
  }, [detailData, selectedVersionId]);

  useEffect(() => {
    if (!detailOpen) return;
    setEditOpen(false);
    setEditRotate(0);
    setEditCropMode(false);
    setEditCropApplied(false);
    setEditCropRect({ x: 0, y: 0, w: 0, h: 0 });
    setEditImageMeta({ naturalW: 0, naturalH: 0, displayW: 0, displayH: 0 });
    setEditError("");
  }, [detailOpen, selectedVersionId]);

  const projects = useMemo(() => {
    const unique = new Map();
    items.forEach((item) => {
      const name = item.projectName || "Sans projet";
      unique.set(name, name);
    });
    return ["all", ...Array.from(unique.keys())];
  }, [items]);

  const visibleItems = useMemo(() => {
    const filtered = items.filter((item) => {
      if (typeFilter !== "all" && item.type !== typeFilter) return false;
      if (sourceFilter === "montage" && item.source !== "montage") return false;
      if (sourceFilter === "standard" && item.source === "montage") return false;
      if (sourceTab !== "public" && projectFilter !== "all" && item.projectName !== projectFilter) return false;
      return true;
    });
    const sorted = filtered.sort((a, b) => {
      const aTime = parseDate(a.createdAt);
      const bTime = parseDate(b.createdAt);
      return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
    });
    return sorted;
  }, [items, projectFilter, sortOrder, sourceTab, typeFilter]);

  function toggleSelection(entry) {
    if (!isPicker || !entry?.url) return;
    const key = `${entry.mediaItemId || ""}:${entry.mediaVersionId || ""}:${entry.url}`;
    setSelectedEntries((prev) => {
      const exists = prev.some(
        (item) => `${item.mediaItemId || ""}:${item.mediaVersionId || ""}:${item.url}` === key
      );
      if (exists) {
        return prev.filter(
          (item) => `${item.mediaItemId || ""}:${item.mediaVersionId || ""}:${item.url}` !== key
        );
      }
      if (!pickerMulti) return [entry];
      return [...prev, entry];
    });
  }

  async function openDetail(item) {
    if (!item) return;
    setDetailItem(item);
    setDetailData(null);
    setDetailError("");
    setDetailTitle(item.title || "");
    setDetailProjectId(item.projectId || "");
    setDetailProjectName(item.projectName || "");
    setDetailEditTitle(false);
    setDetailEditProject(false);
    setDetailOpen(true);
    if (!item.mediaItemId && !item.assetId) {
      setDetailError("Detail indisponible pour ce media.");
      return;
    }
    setDetailLoading(true);
    try {
      const query = item.mediaItemId
        ? `media_item_id=${encodeURIComponent(item.mediaItemId)}`
        : `asset_id=${encodeURIComponent(item.assetId)}`;
      const res = await fetch(`/api/fast/media/detail?${query}`);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur detail media.");
      setDetailData(data);
      const itemView = data.item_view || data.item || {};
      setDetailTitle(itemView.title || "");
      setDetailProjectId(itemView.project_id || data?.item?.project_id || "");
      setDetailProjectName(itemView.project_name || "");
      setDetailItem((prev) => ({
        ...(prev || {}),
        mediaItemId: data?.item?.media_item_id || prev?.mediaItemId || "",
        assetId: itemView.id || prev?.assetId || "",
        url: itemView.url || prev?.url || ""
      }));
      const initialVersionId =
        data?.item_view?.version_id ||
        data?.item?.current_version_id ||
        data?.versions?.[0]?.version?.media_version_id ||
        "";
      setSelectedVersionId(initialVersionId);
    } catch (err) {
      setDetailError(err.message || "Erreur detail media.");
    } finally {
      setDetailLoading(false);
    }
  }

  function toggleSlideshowItem(item) {
    if (!item.mediaItemId) return;
    setSlideshowSelected((prev) => {
      const exists = prev.some((id) => id === item.mediaItemId);
      return exists ? prev.filter((id) => id !== item.mediaItemId) : [...prev, item.mediaItemId];
    });
  }

  async function generateSlideshow() {
    if (!slideshowSelected.length || slideshowLoading) return;
    setSlideshowLoading(true);
    setSlideshowMsg("");
    try {
      const res = await fetch("/api/fast/media/slideshow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ media_item_ids: slideshowSelected })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || data.error || "Erreur generation slideshow.");
      setSlideshowMsg("Slideshow généré ! Il apparaîtra dans vos médias.");
      setSlideshowSelected([]);
      setSelectMode(false);
      // Reload media to show the new video
      const endpoint = sourceTab === "public"
        ? "/api/fast/media/public/assets"
        : sourceTab === "uploads"
          ? "/api/fast/media/uploads"
          : "/api/fast/media/assets";
      const reloadRes = await fetch(endpoint);
      if (reloadRes.ok) {
        const reloadData = await reloadRes.json();
        const rawItems = Array.isArray(reloadData.items) ? reloadData.items : [];
        const normalized = rawItems
          .filter((entry) => entry?.url)
          .map((entry) => ({
            id: entry.media_item_id || entry.id || entry.url,
            mediaItemId: entry.media_item_id || "",
            mediaVersionId: entry.version_id || entry.current_version_id || "",
            assetId: entry.asset_id || entry.id || "",
            url: entry.url,
            thumbnailUrl: entry.thumbnail_url || "",
            type: entry.type || guessTypeFromUrl(entry.url),
            source: entry.source || "",
            projectId: entry.project_id || "",
            projectName: entry.project_name || "Sans projet",
            title: entry.title || "",
            createdAt: entry.created_at || "",
            updatedAt: entry.updated_at || ""
          }));
        setItems(normalized);
      }
    } catch (err) {
      setSlideshowMsg(err.message || "Erreur inconnue.");
    } finally {
      setSlideshowLoading(false);
    }
  }

  function closeDetail() {
    setDetailOpen(false);
    setDetailItem(null);
    setDetailData(null);
    setDetailError("");
    setDetailTitle("");
    setDetailSaving(false);
    setDetailEditTitle(false);
    setDetailProjectId("");
    setDetailProjectName("");
    setDetailEditProject(false);
    setSelectedVersionId("");
    setDeleteOpen(false);
    setDeleteLoading(false);
    setDeleteItemOpen(false);
    setDeleteItemLoading(false);
  }

  function formatParamValue(value) {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  }

  async function saveDetailTitle() {
    if (!detailItem?.mediaItemId || detailSaving) return;
    setDetailSaving(true);
    try {
      const res = await fetch("/api/fast/media/title", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ media_item_id: detailItem.mediaItemId, title: detailTitle })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur sauvegarde titre.");
      setDetailData((prev) => prev ? { ...prev, item: { ...prev.item, title: detailTitle } } : prev);
      setItems((prev) =>
        prev.map((entry) =>
          entry.mediaItemId === detailItem.mediaItemId ? { ...entry, title: detailTitle } : entry
        )
      );
      setDetailEditTitle(false);
    } catch (err) {
      setDetailError(err.message || "Erreur sauvegarde titre.");
    } finally {
      setDetailSaving(false);
    }
  }

  async function saveDetailProject() {
    if (!detailItem?.mediaItemId || detailSaving) return;
    setDetailSaving(true);
    try {
      const res = await fetch("/api/fast/media/project", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ media_item_id: detailItem.mediaItemId, project_id: detailProjectId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur sauvegarde projet.");
      const name = data?.saved?.project_name || "";
      setDetailProjectName(name);
      setDetailData((prev) =>
        prev
          ? { ...prev, item: { ...prev.item, project_id: detailProjectId, project_name: name } }
          : prev
      );
      setItems((prev) =>
        prev.map((entry) =>
          entry.mediaItemId === detailItem.mediaItemId
            ? { ...entry, projectId: detailProjectId, projectName: name }
            : entry
        )
      );
      setDetailEditProject(false);
    } catch (err) {
      setDetailError(err.message || "Erreur sauvegarde projet.");
    } finally {
      setDetailSaving(false);
    }
  }

  async function deleteMediaVersion() {
    if (!detailItem?.mediaItemId || !selectedVersionId || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const res = await fetch("/api/fast/media/version/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          media_item_id: detailItem.mediaItemId,
          media_version_id: selectedVersionId
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur suppression.");
      setDetailData((prev) => {
        if (!prev) return prev;
        const nextVersions = (prev.versions || []).filter(
          (entry) => entry?.version?.media_version_id !== selectedVersionId
        );
        return { ...prev, versions: nextVersions };
      });
      const nextVersionId = data.current_version_id || "";
      setSelectedVersionId(nextVersionId);
      if (!nextVersionId) {
        setItems((prev) => prev.filter((entry) => entry.mediaItemId !== detailItem.mediaItemId));
        closeDetail();
        return;
      }
      openDetail(detailItem);
    } catch (err) {
      setDetailError(err.message || "Erreur suppression.");
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  }

  async function deleteMediaItem() {
    if (!detailItem?.mediaItemId || deleteItemLoading) return;
    setDeleteItemLoading(true);
    try {
      const res = await fetch("/api/fast/media/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ media_item_id: detailItem.mediaItemId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur suppression.");
      setItems((prev) => prev.filter((entry) => entry.mediaItemId !== detailItem.mediaItemId));
      closeDetail();
    } catch (err) {
      setDetailError(err.message || "Erreur suppression.");
    } finally {
      setDeleteItemLoading(false);
      setDeleteItemOpen(false);
    }
  }

  async function restoreArchivedProject() {
    if (!archivedProjectId || archivedActionLoading) return;
    if (!window.confirm(t("media.archived.restoreConfirm"))) return;
    setArchivedActionLoading(true);
    try {
      const res = await fetch("/api/fast/projects/restore", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: archivedProjectId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur restauration.");
      const remaining = archivedProjects.filter((p) => p.project_id !== archivedProjectId);
      setArchivedProjects(remaining);
      setArchivedProjectId(remaining[0]?.project_id || "");
    } catch (err) {
      setErrorMsg(err.message || "Erreur restauration.");
    } finally {
      setArchivedActionLoading(false);
    }
  }

  async function deleteArchivedProject() {
    if (!archivedProjectId || archivedActionLoading) return;
    if (!window.confirm(t("media.archived.deleteConfirm"))) return;
    setArchivedActionLoading(true);
    try {
      const res = await fetch("/api/fast/projects/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: archivedProjectId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Erreur suppression projet.");
      const remaining = archivedProjects.filter((p) => p.project_id !== archivedProjectId);
      setArchivedProjects(remaining);
      setArchivedProjectId(remaining[0]?.project_id || "");
      setItems([]);
    } catch (err) {
      setErrorMsg(err.message || "Erreur suppression projet.");
    } finally {
      setArchivedActionLoading(false);
    }
  }

  function copyToClipboard(value) {
    const text = typeof value === "string" ? value : formatParamValue(value);
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
  }

  function openVideoWithRecommended(generation, promptVersion) {
    const params = generation?.parameters || {};
    const recommended =
      promptVersion?.recommended ||
      promptVersion?.validation_report?.ai_recommended ||
      null;
    if (!recommended) {
      setDetailError("Prompt recommande indisponible.");
      return;
    }
    const finalPrompt =
      generation.compiled_prompt ||
      params.prompt_final ||
      generation.prompt ||
      "";
    const fineTuning = recommended.fine_tuning || {};
    const suggestedTags =
      Array.isArray(recommended.tags_suggeres) && recommended.tags_suggeres.length
        ? recommended.tags_suggeres
        : promptVersion?.selections?.tag_ids || params.tags || [];
    const state = {
      mode: params.mode || "text",
      modelId: generation.model || "",
      ratio: params.aspect_ratio || "9:16",
      resolution: params.resolution || "720p",
      duration: params.duration_seconds || 4,
      promptText: finalPrompt,
      selectedTags: suggestedTags,
      selectedPresets: promptVersion?.selections?.preset_ids || [],
      grain: typeof fineTuning.grain === "number" ? fineTuning.grain : params.grain ?? 15,
      contrast: typeof fineTuning.contrast === "number" ? fineTuning.contrast : params.contrast ?? 60,
      expertMode: "expert",
      narrative: {
        subject: recommended?.narrative?.subject || "",
        action: recommended?.narrative?.action || finalPrompt,
        environment: recommended?.narrative?.environment || "",
        temporal_progression: recommended?.narrative?.temporal_progression || ""
      },
      cinematography: {
        shot_type: recommended?.cinematography?.shot_type || "",
        camera_motion: recommended?.cinematography?.camera_motion || "",
        lens: recommended?.cinematography?.lens || "",
        focus: recommended?.cinematography?.focus || "",
        framing: recommended?.cinematography?.framing || ""
      },
      visualStyle: {
        lighting: recommended?.visual_style?.lighting || "",
        color_palette: recommended?.visual_style?.color_palette || "",
        mood: recommended?.visual_style?.mood || "",
        art_direction: recommended?.visual_style?.art_direction || ""
      },
      constraints: {
        identity_consistency: recommended?.constraints?.identity_consistency ?? true,
        no_style_drift: recommended?.constraints?.no_style_drift ?? true,
        no_character_merge: recommended?.constraints?.no_character_merge ?? true
      },
      inputImage: params.input_image_url || "",
      referenceImages: params.reference_image_urls || [],
      startImage: params.start_image_url || "",
      endImage: params.end_image_url || "",
      inputVideo: params.input_video_url || "",
      resultUrl: "",
      compiledPrompt: ""
    };
    try {
      window.sessionStorage.setItem("contentos_video_state", JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
    window.location.href = "/video";
  }

  function collectInputMedia(params) {
    const collected = []
      .concat(params?.input_urls || [])
      .concat(params?.reference_image_urls || [])
      .concat(params?.input_image_url ? [params.input_image_url] : [])
      .concat(params?.start_image_url ? [params.start_image_url] : [])
      .concat(params?.end_image_url ? [params.end_image_url] : [])
      .concat(params?.input_video_url ? [params.input_video_url] : []);
    return Array.from(new Set(collected.filter(Boolean))).map((url) => ({
      url,
      type: guessTypeFromUrl(url)
    }));
  }

  async function confirmSelection() {
    try {
      const res = await fetch("/api/fast/media/selection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_paths: selectedUrls, target: pickerTarget })
      });
      if (!res.ok) throw new Error("Impossible d'enregistrer la selection.");
      try {
        window.sessionStorage.setItem(
          "contentos_media_picker_payload",
          JSON.stringify({
            target: pickerTarget,
            images: selectedUrls,
            versions: selectedEntries
          })
        );
      } catch {
        // Ignore storage errors.
      }
      const targetParam = encodeURIComponent(pickerTarget);
      const returnUrl = pickerReturn || "/";
      const joiner = returnUrl.includes("?") ? "&" : "?";
      window.location.href = `${returnUrl}${joiner}media_picker_done=1&target=${targetParam}`;
    } catch (err) {
      setErrorMsg(err.message || "Erreur lors de l'enregistrement.");
    }
  }

  function cancelSelection() {
    const targetParam = encodeURIComponent(pickerTarget);
    const returnUrl = pickerReturn || "/";
    const joiner = returnUrl.includes("?") ? "&" : "?";
    window.location.href = `${returnUrl}${joiner}media_picker_cancel=1&target=${targetParam}`;
  }

  async function handleUpload(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const res = await fetch("/api/fast/media/uploads", {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error("Upload echoue.");
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      if (newItems.length) {
        setItems((prev) => {
          const merged = [...newItems, ...prev];
          const seen = new Set();
          return merged.filter((item) => {
            const key = item.id || item.url;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      }
    } catch (err) {
      setUploadError(err.message || "Upload echoue.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function handleExport() {
    if (!activeMediaUrl || activeMediaType !== "image") return;

    setExportLoading(true);
    try {
      const response = await fetch("/api/fast/media/export", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_url: activeMediaUrl,
          format: exportFormat,
          size: exportSize
        })
      });

      if (!response.ok) throw new Error("Export failed");

      const data = await response.json();
      if (data.url) {
        // Download the exported file
        const link = document.createElement("a");
        link.href = data.url;
        link.download = `export_${Date.now()}.${exportFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setExportOpen(false);
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  }

  // Get available export sizes based on image dimensions
  function getAvailableSizes(imageUrl) {
    // This would ideally get actual image dimensions
    // For now, return all options
    return [
      { value: "original", label: "Original" },
      { value: "4k", label: "4K (3840x2160)" },
      { value: "2k", label: "2K (2560x1440)" },
      { value: "1k", label: "1K (1920x1080)" }
    ];
  }


  const detailVersions = detailData?.versions || [];
  const activeVersionEntry =
    detailVersions.find((entry) => entry?.version?.media_version_id === selectedVersionId) ||
    detailVersions[0] ||
    null;
  const activeAsset = activeVersionEntry?.asset || {};
  const activeGenerations = activeVersionEntry?.generations || [];
  const activeMediaUrl = activeAsset?.url || detailItem?.url || "";
  const activeMediaType = activeAsset?.type || detailItem?.type || guessTypeFromUrl(activeMediaUrl);
  const activePreviewUrl =
    activeMediaType === "model"
      ? (activeAsset?.thumbnail_url || detailItem?.thumbnailUrl || "")
      : activeMediaUrl;
  const currentVersionId = detailData?.item?.current_version_id || detailData?.item_view?.current_version_id || "";

  function MediaDetailModal() {
    if (!detailOpen) return null;
    return (
      <div className="media-detail-overlay" onClick={closeDetail}>
        <div className="media-detail" onClick={(event) => event.stopPropagation()}>
          <button className="media-detail-close" type="button" onClick={closeDetail} aria-label="Fermer">
            <span className="material-symbols-rounded">close</span>
          </button>
          <div className="media-detail-body">
            <div className="media-detail-left">
              <div className="media-detail-title-row">
                {detailTitle && !detailEditTitle ? (
                  <>
                    <h3 className="media-detail-title-text">{detailTitle}</h3>
                    <button
                      type="button"
                      className="media-detail-icon"
                      onClick={() => setDetailEditTitle(true)}
                      aria-label="Editer"
                    >
                      <span className="material-symbols-rounded">edit</span>
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      value={detailTitle}
                      onChange={(event) => setDetailTitle(event.target.value)}
                      placeholder="Ajouter un titre"
                    />
                    <button type="button" onClick={saveDetailTitle} disabled={detailSaving}>
                      {detailSaving ? "..." : "Enregistrer"}
                    </button>
                  </>
                )}
              </div>
              <div className="media-detail-project-row">
                {!detailEditProject ? (
                  <>
                    <p className="media-detail-project-text">
                      {detailProjectName || "Sans projet"}
                    </p>
                    <button
                      type="button"
                      className="media-detail-icon"
                      onClick={() => setDetailEditProject(true)}
                      aria-label="Editer projet"
                    >
                      <span className="material-symbols-rounded">edit</span>
                    </button>
                  </>
                ) : (
                  <>
                    <select
                      value={detailProjectId}
                      onChange={(event) => setDetailProjectId(event.target.value)}
                    >
                      <option value="">Sans projet</option>
                      {projectOptions.map((project) => (
                        <option key={project.project_id} value={project.project_id}>
                          {project.name || project.project_id}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={saveDetailProject} disabled={detailSaving}>
                      {detailSaving ? "..." : "Enregistrer"}
                    </button>
                  </>
                )}
              </div>
              {activeMediaUrl && activeMediaType === "video" && (
                <video src={activeMediaUrl} controls preload="metadata" />
              )}
              {activePreviewUrl && activeMediaType === "model" && (
                <img src={activePreviewUrl} alt="model detail" />
              )}
              {activeMediaUrl && activeMediaType !== "video" && activeMediaType !== "model" && (
                <img src={activeMediaUrl} alt="media detail" />
              )}
              {activeMediaUrl && (
                <div className="media-detail-actions">
                  {activeMediaType === "image" && (
                    <>
                      <button
                        type="button"
                        className="media-detail-edit"
                        onClick={() => setEditOpen(true)}
                      >
                        <span className="material-symbols-rounded">tune</span>
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="media-detail-export"
                        onClick={() => setExportOpen(true)}
                      >
                        <span className="material-symbols-rounded">file_download</span>
                        Exporter
                      </button>
                    </>
                  )}
                  {activeMediaType === "image" ? (
                    <>
                      <button
                        type="button"
                        className="media-detail-download"
                        onClick={() => downloadFile(activeMediaUrl, buildDownloadName(activeMediaUrl))}
                      >
                        <span className="material-symbols-rounded">download</span>
                        Original
                      </button>
                      <button
                        type="button"
                        className="media-detail-download"
                        onClick={async () => {
                          const apiUrl = buildDownloadUrl(activeMediaUrl, "webp");
                          const res = await fetch(apiUrl);
                          const blob = await res.blob();
                          const objectUrl = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = objectUrl;
                          a.download = buildDownloadName(activeMediaUrl).replace(/\.[^.]+$/, ".webp");
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
                        }}
                      >
                        <span className="material-symbols-rounded">image</span>
                        WebP 80%
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="media-detail-download"
                      onClick={() => downloadFile(activeMediaUrl, buildDownloadName(activeMediaUrl))}
                    >
                      <span className="material-symbols-rounded">download</span>
                      Télécharger
                    </button>
                  )}
                  <button
                    type="button"
                    className="media-detail-delete"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <span className="material-symbols-rounded">delete</span>
                    Supprimer
                  </button>
                  <button
                    type="button"
                    className="media-detail-split"
                    onClick={() => {
                      if (!detailItem?.mediaItemId || !selectedVersionId) return;
                      fetch("/api/fast/media/version/split", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          media_item_id: detailItem.mediaItemId,
                          media_version_id: selectedVersionId
                        })
                      })
                        .then((res) => res.json())
                        .then((data) => {
                          if (!data?.ok) return;
                          openDetail(detailItem);
                        })
                        .catch(() => { });
                    }}
                  >
                    <span className="material-symbols-rounded">call_split</span>
                    Separer
                  </button>
                  <button
                    type="button"
                    className="media-detail-delete-full"
                    onClick={() => setDeleteItemOpen(true)}
                  >
                    <span className="material-symbols-rounded">delete_forever</span>
                    Supprimer le media complet
                  </button>
                </div>
              )}
            </div>
            <div className="media-detail-right">
              <h3>Versions</h3>
              {detailLoading && <p className="media-muted">Chargement...</p>}
              {detailError && <p className="media-error">{detailError}</p>}
              {!detailLoading && !detailError && (
                <>
                  {(detailData?.versions || []).length === 0 && (
                    <p className="media-muted">Aucune version disponible.</p>
                  )}
                  <div className="media-version-grid">
                    {(detailData?.versions || []).map((entry) => {
                      const version = entry.version || {};
                      const asset = entry.asset || {};
                      const versionLabel = version.version_index
                        ? `Version ${version.version_index}`
                        : "Version";
                      const mediaItemId = detailData?.item?.media_item_id || detailItem?.mediaItemId || "";
                      const selectionEntry = {
                        url: asset.url || "",
                        mediaItemId,
                        mediaVersionId: version.media_version_id || "",
                        assetId: asset.id || ""
                      };
                      const versionSelected = selectedEntries.some(
                        (sel) => sel.mediaVersionId === selectionEntry.mediaVersionId
                      );
                      const isActive = version.media_version_id === selectedVersionId;
                      const isCurrent = version.media_version_id === currentVersionId;
                      return (
                        <div
                          key={version.media_version_id || version.asset_id || versionLabel}
                          className={`media-version-card${isActive ? " active" : ""}`}
                          onClick={() => setSelectedVersionId(version.media_version_id || "")}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setSelectedVersionId(version.media_version_id || "");
                            }
                          }}
                        >
                          <div className="media-version-card-head">
                            <span className="media-detail-label">
                              {versionLabel}{version.label ? ` • ${version.label}` : ""}
                            </span>
                            {isPicker && asset.url && (
                              <button
                                type="button"
                                className="media-version-select"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleSelection(selectionEntry);
                                }}
                              >
                                {versionSelected ? "Retirer" : "Utiliser"}
                              </button>
                            )}
                          </div>
                          <div className="media-version-card-actions">
                            <button
                              type="button"
                              className="media-version-primary"
                              disabled={isCurrent}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!detailItem?.mediaItemId || !version.media_version_id) return;
                                fetch("/api/fast/media/version/current", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    media_item_id: detailItem.mediaItemId,
                                    media_version_id: version.media_version_id
                                  })
                                })
                                  .then((res) => res.json())
                                  .then((data) => {
                                    if (!data?.ok) return;
                                    setSelectedVersionId(version.media_version_id);
                                    openDetail(detailItem);
                                  })
                                  .catch(() => { });
                              }}
                            >
                              {isCurrent ? "Version principale" : "Definir principale"}
                            </button>
                          </div>
                          <div className="media-version-thumb">
                            {asset.url ? (
                              asset.type === "video" ? (
                                asset.thumbnail_url ? (
                                  <img src={asset.thumbnail_url} alt="Video thumbnail" />
                                ) : (
                                  <video src={asset.url} controls preload="metadata" />
                                )
                              ) : (
                                <img src={asset.thumbnail_url || asset.url} alt="version media" />
                              )
                            ) : (
                              <span className="media-muted">Aucun fichier</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {activeVersionEntry && (
                    <div className="media-version-info">
                      <h4>Version selectionnee</h4>
                      {activeGenerations.length === 0 && <p className="media-muted">Aucune generation liee.</p>}
                      {activeGenerations.map((genEntry, idx) => {
                        const generation = genEntry.generation || {};
                        const params = generation.parameters || {};
                        const promptVersion = genEntry.prompt_version || {};
                        const inputs = collectInputMedia(params);
                        return (
                          <div key={generation.generation_id || idx} className="media-detail-block">
                            <div className="media-detail-block-head">
                              <span className="media-detail-label">Generation {idx + 1}</span>
                            </div>
                            <div className="media-detail-block">
                              <div className="media-detail-block-head">
                                <span className="media-detail-label">Prompt initial</span>
                                <button
                                  type="button"
                                  className="media-detail-copy"
                                  onClick={() =>
                                    copyToClipboard(params.prompt_initial || generation.prompt || "")
                                  }
                                  aria-label="Copier"
                                >
                                  <span className="material-symbols-rounded">content_copy</span>
                                </button>
                              </div>
                              <p>{params.prompt_initial || generation.prompt || "-"}</p>
                            </div>
                            <div className="media-detail-block">
                              <div className="media-detail-block-head">
                                <span className="media-detail-label">Prompt final</span>
                                <button
                                  type="button"
                                  className="media-detail-copy"
                                  onClick={() =>
                                    copyToClipboard(params.prompt_final || generation.prompt || "")
                                  }
                                  aria-label="Copier"
                                >
                                  <span className="material-symbols-rounded">content_copy</span>
                                </button>
                              </div>
                              <p>{params.prompt_final || generation.prompt || "-"}</p>
                            </div>
                            <div className="media-detail-block">
                              <span className="media-detail-label">Tags</span>
                              <p>{(params.tags || []).join(", ") || "-"}</p>
                            </div>
                            <div className="media-detail-block">
                              <span className="media-detail-label">Medias d'entree</span>
                              {inputs.length ? (
                                <div className="media-detail-inputs">
                                  {inputs.map((media) => (
                                    <div key={media.url} className="media-detail-input">
                                      <div className="media-detail-thumb">
                                        {media.type === "video" ? (
                                          <video src={media.url} muted />
                                        ) : (
                                          <img src={media.url} alt="input media" />
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        className="media-detail-link"
                                        onClick={() => downloadFile(media.url, buildDownloadName(media.url))}
                                      >
                                        {media.url}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p>-</p>
                              )}
                            </div>
                            <div className="media-detail-block">
                              <div className="media-detail-block-head">
                                <span className="media-detail-label">Parametres</span>
                                <button
                                  type="button"
                                  className="media-detail-copy"
                                  onClick={() =>
                                    copyToClipboard(
                                      Object.fromEntries(
                                        Object.entries(params || {}).filter(
                                          ([key]) => !["prompt_initial", "prompt_final", "tags"].includes(key)
                                        )
                                      )
                                    )
                                  }
                                  aria-label="Copier"
                                >
                                  <span className="material-symbols-rounded">content_copy</span>
                                </button>
                              </div>
                              <pre>
                                {formatParamValue(
                                  Object.fromEntries(
                                    Object.entries(params || {}).filter(
                                      ([key]) => !["prompt_initial", "prompt_final", "tags"].includes(key)
                                    )
                                  )
                                ) || "-"}
                              </pre>
                            </div>
                            <div className="media-detail-block">
                              <div className="media-detail-block-head">
                                <span className="media-detail-label">Prompt version</span>
                                <button
                                  type="button"
                                  className="media-detail-copy"
                                  onClick={() => copyToClipboard(promptVersion || "")}
                                  aria-label="Copier"
                                >
                                  <span className="material-symbols-rounded">content_copy</span>
                                </button>
                              </div>
                              <pre>{formatParamValue(promptVersion || "-")}</pre>
                            </div>
                            <div className="media-detail-block">
                              <div className="media-detail-block-head">
                                <span className="media-detail-label">Prompt recommended</span>
                                <div className="media-detail-block-actions">
                                  <button
                                    type="button"
                                    className="media-detail-load"
                                    onClick={() => openVideoWithRecommended(generation, promptVersion)}
                                    disabled={!promptVersion?.recommended && !promptVersion?.validation_report?.ai_recommended}
                                  >
                                    Charger dans Video
                                  </button>
                                  <button
                                    type="button"
                                    className="media-detail-copy"
                                    onClick={() =>
                                      copyToClipboard(
                                        promptVersion?.recommended ||
                                        promptVersion?.validation_report?.ai_recommended ||
                                        ""
                                      )
                                    }
                                    aria-label="Copier"
                                  >
                                    <span className="material-symbols-rounded">content_copy</span>
                                  </button>
                                </div>
                              </div>
                              <pre>
                                {formatParamValue(
                                  promptVersion?.recommended ||
                                  promptVersion?.validation_report?.ai_recommended ||
                                  "-"
                                )}
                              </pre>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {deleteOpen && (
          <div className="media-confirm">
            <div className="media-confirm-card">
              <h4>Supprimer cette version ?</h4>
              <p>Cette action est definitive.</p>
              <div className="media-confirm-actions">
                <button type="button" onClick={() => setDeleteOpen(false)}>
                  Annuler
                </button>
                <button type="button" className="danger" onClick={deleteMediaVersion} disabled={deleteLoading}>
                  {deleteLoading ? "Suppression..." : "Supprimer"}
                </button>
              </div>
            </div>
          </div>
        )}
        {deleteItemOpen && (
          <div className="media-confirm">
            <div className="media-confirm-card">
              <h4>Supprimer ce media ?</h4>
              <p>Cette action est definitive.</p>
              <div className="media-confirm-actions">
                <button type="button" onClick={() => setDeleteItemOpen(false)}>
                  Annuler
                </button>
                <button type="button" className="danger" onClick={deleteMediaItem} disabled={deleteItemLoading}>
                  {deleteItemLoading ? "Suppression..." : "Supprimer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={isPicker ? "app-shell app-shell-picker" : "app-shell"}>
      {!isPicker && <Sidebar active="media" />}
      <div className="app-main">
        <div className={isPicker ? "media-page picker-mode" : "media-page"}>
          <header className="media-header">
            <div>
              <p className="media-kicker">
                {isArchivedRoute ? t("media.archived.kicker") : t("media.kicker")}
              </p>
              <h1 className="media-title page-title-accent">
                {isArchivedRoute ? t("media.archived.title") : t("media.title")}
              </h1>
              <p className="media-subtitle">
                {isArchivedRoute ? t("media.archived.subtitle") : t("media.subtitle")}
              </p>
            </div>
            <div className="media-tabs">
              {!isArchivedRoute && (
                <>
                  <button
                    className={sourceTab === "mine" ? "media-tab active" : "media-tab"}
                    onClick={() => setSourceTab("mine")}
                  >
                    {t("media.tab.mine")}
                  </button>
                  {isPicker && (
                    <button
                      className={sourceTab === "uploads" ? "media-tab active" : "media-tab"}
                      onClick={() => setSourceTab("uploads")}
                    >
                      {t("media.tab.uploads")}
                    </button>
                  )}
                </>
              )}
              {isArchivedRoute ? (
                <a className="media-archive-link" href="/media">
                  {t("media.archived.back")}
                </a>
              ) : (
                !isPicker && (
                  <>
                    <a className="media-archive-link" href="/media/archived">
                      {t("media.archived.link")}
                    </a>
                    <button
                      type="button"
                      className={selectMode ? "media-tab active" : "media-tab"}
                      onClick={() => {
                        setSelectMode((prev) => !prev);
                        setSlideshowSelected([]);
                        setSlideshowMsg("");
                      }}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }}>photo_library</span>
                      Slideshow
                    </button>
                  </>
                )
              )}
            </div>
            {isPicker && (
              <div className="media-picker-actions">
                <span className="media-picker-count">
                  {selectedEntries.length} {t("media.picker.count")}
                </span>
                <button className="media-btn secondary" onClick={cancelSelection}>
                  {t("common.cancel")}
                </button>
                <button className="media-btn" disabled={!selectedUrls.length} onClick={confirmSelection}>
                  {t("media.picker.use")}
                </button>
              </div>
            )}
            {selectMode && !isPicker && (
              <div className="media-slideshow-bar">
                <span className="media-slideshow-count">
                  <span className="material-symbols-rounded">check_circle</span>
                  {slideshowSelected.length} image{slideshowSelected.length !== 1 ? "s" : ""} sélectionnée{slideshowSelected.length !== 1 ? "s" : ""}
                </span>
                {slideshowMsg && <span className="media-slideshow-msg">{slideshowMsg}</span>}
                <button
                  type="button"
                  className="media-btn secondary"
                  onClick={() => { setSelectMode(false); setSlideshowSelected([]); setSlideshowMsg(""); }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="media-btn"
                  disabled={slideshowSelected.length < 2 || slideshowLoading}
                  onClick={generateSlideshow}
                >
                  {slideshowLoading
                    ? <><span className="material-symbols-rounded" style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }}>hourglass_top</span>Génération...</>
                    : <><span className="material-symbols-rounded" style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }}>movie</span>Générer la vidéo</>
                  }
                </button>
              </div>
            )}
          </header>

          {isPicker && sourceTab === "uploads" && (
            <section className="media-upload-panel">
              <div className="media-upload-title">{t("media.uploads.title")}</div>
              <p className="media-upload-subtitle">{t("media.uploads.subtitle")}</p>
              <input
                type="file"
                multiple
                accept={
                  pickerType === "image"
                    ? "image/*"
                    : pickerType === "video"
                      ? "video/*"
                      : pickerType === "audio"
                        ? "audio/*"
                        : "*/*"
                }
                onChange={handleUpload}
                disabled={uploading}
              />
              {uploadError && <div className="media-upload-error">{uploadError}</div>}
            </section>
          )}

          {isArchivedRoute && (
            <section className="media-archived">
              <div className="media-archive-tags">
                {archivedProjects.length === 0 && (
                  <span className="media-muted">{t("media.archived.empty")}</span>
                )}
                {archivedProjects.map((project) => (
                  <button
                    key={project.project_id}
                    type="button"
                    className={
                      project.project_id === archivedProjectId
                        ? "media-archive-tag active"
                        : "media-archive-tag"
                    }
                    onClick={() => setArchivedProjectId(project.project_id || "")}
                  >
                    {project.name || project.project_id}
                  </button>
                ))}
              </div>
              <div className="media-archive-actions">
                <button
                  className="media-btn secondary"
                  disabled={!archivedProjectId || archivedActionLoading}
                  onClick={restoreArchivedProject}
                >
                  {t("media.archived.restore")}
                </button>
                <button
                  className="media-btn danger"
                  disabled={!archivedProjectId || archivedActionLoading}
                  onClick={deleteArchivedProject}
                >
                  {t("media.archived.delete")}
                </button>
              </div>
            </section>
          )}

          <section className="media-controls">
            <div className="media-filter">
              <label htmlFor="media-type">{t("media.filter.type")}</label>
              <select id="media-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                {Object.entries(TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {!isArchivedRoute && sourceTab !== "public" && (
              <div className="media-filter">
                <label htmlFor="media-project">{t("media.filter.project")}</label>
                <select
                  id="media-project"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <option value="all">{TYPE_LABELS.all}</option>
                  {projects
                    .filter((project) => project !== "all")
                    .map((project) => (
                      <option key={project} value={project}>
                        {project}
                      </option>
                    ))}
                </select>
              </div>
            )}
            <div className="media-filter">
              <label htmlFor="media-source">{t("media.filter.source")}</label>
              <select id="media-source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="all">{t("media.filter.source.all")}</option>
                <option value="montage">{t("media.filter.source.montage")}</option>
                <option value="standard">{t("media.filter.source.standard")}</option>
              </select>
            </div>
            <div className="media-filter">
              <label htmlFor="media-sort">{t("media.filter.sort")}</label>
              <select id="media-sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="newest">{t("media.filter.sort.new")}</option>
                <option value="oldest">{t("media.filter.sort.old")}</option>
              </select>
            </div>
          </section>

          <section className="media-grid">
            {loading && <div className="media-state">{t("media.state.loading")}</div>}
            {!loading && errorMsg && <div className="media-state error">{errorMsg}</div>}
            {!loading && !errorMsg && visibleItems.length === 0 && (
              <div className="media-state">{t("media.state.empty")}</div>
            )}
            {!loading &&
              !errorMsg &&
              visibleItems.map((item) => {
                const entryKey = `${item.mediaItemId || ""}:${item.mediaVersionId || ""}:${item.url}`;
                const isSelected = selectedEntries.some(
                  (entry) =>
                    `${entry.mediaItemId || ""}:${entry.mediaVersionId || ""}:${entry.url}` === entryKey
                );
                const selectedCount = item.mediaItemId
                  ? selectedEntries.filter((entry) => entry.mediaItemId === item.mediaItemId).length
                  : (isSelected ? 1 : 0);
                const cardSelected = selectedCount > 0;
                const isSlideshowPicked = selectMode && slideshowSelected.includes(item.mediaItemId);
                return (
                  <article
                    key={item.id}
                    className={`media-card${cardSelected ? " selected" : ""}${isSlideshowPicked ? " slideshow-picked" : ""}`}
                  >
                    <div className="media-thumb">
                      <button
                        type="button"
                        className="media-thumb-overlay"
                        onClick={() => selectMode ? toggleSlideshowItem(item) : openDetail(item)}
                        aria-label={selectMode ? "Sélectionner pour slideshow" : "Voir le media"}
                      />
                      {selectMode && item.type === "image" && (
                        <button
                          type="button"
                          className={`media-slideshow-check${isSlideshowPicked ? " picked" : ""}`}
                          onClick={(event) => { event.stopPropagation(); toggleSlideshowItem(item); }}
                          aria-label="Sélectionner pour slideshow"
                        >
                          <span className="material-symbols-rounded">{isSlideshowPicked ? "check_circle" : "radio_button_unchecked"}</span>
                        </button>
                      )}
                      {isPicker && (
                        <button
                          type="button"
                          className="media-select-indicator"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSelection(item);
                          }}
                          aria-label="Selectionner cette version"
                        >
                          {selectedCount > 0 ? selectedCount : "+"}
                        </button>
                      )}
                      {item.type === "video" && (
                        <img src={item.thumbnailUrl || item.url} alt="Video preview" />
                      )}
                      {item.type === "audio" && (
                        <div className="media-audio">
                          <audio src={item.url} controls />
                        </div>
                      )}
                      {item.type === "model" && (
                        <img src={item.thumbnailUrl || item.url} alt="Modele 3D" />
                      )}
                      {item.type === "image" && (
                        <img src={item.thumbnailUrl || item.url} alt="Media" />
                      )}
                    </div>
                    <div className="media-meta">
                      <span className="media-tag">{TYPE_LABELS[item.type] || item.type}</span>
                      {isPicker && (
                        <button
                          type="button"
                          className="media-select-version"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSelection(item);
                          }}
                        >
                          {isSelected ? "Selectionnee" : "Selectionner cette version"}
                        </button>
                      )}
                      <span className="media-project">{item.projectName || "Sans projet"}</span>
                      {!isPicker && (item.type === "image" || item.type === "video" || item.type === "model") && (
                        <div className="media-action-row">
                          <button
                            type="button"
                            className="media-download"
                            onClick={(event) => { event.stopPropagation(); downloadFile(item.url, buildDownloadName(item.url)); }}
                            title="Telecharger"
                            aria-label="Telecharger"
                          >
                            <span className="material-symbols-rounded">download</span>
                          </button>
                          <button
                            className="media-view"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item);
                            }}
                            title="Vue"
                            aria-label="Vue"
                          >
                            <span className="material-symbols-rounded">visibility</span>
                          </button>
                        </div>
                      )}
                      {item.createdAt && (
                        <span className="media-date">
                          {new Date(item.createdAt).toLocaleDateString("fr-FR", {
                            year: "numeric",
                            month: "short",
                            day: "2-digit"
                          })}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
          </section>
          <MediaDetailModal />
          {exportOpen && (
            <div className="media-edit-overlay" onClick={() => setExportOpen(false)}>
              <div className="media-edit" onClick={(event) => event.stopPropagation()}>
                <div className="media-edit-header">
                  <h3>Exporter l'image</h3>
                  <button
                    type="button"
                    className="media-edit-close"
                    onClick={() => setExportOpen(false)}
                    aria-label="Fermer"
                  >
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </div>
                <div className="media-edit-body">
                  <div className="media-export-options">
                    <div className="media-export-group">
                      <label>Format</label>
                      <div className="media-export-format-buttons">
                        <button
                          type="button"
                          className={exportFormat === "jpeg" ? "active" : ""}
                          onClick={() => setExportFormat("jpeg")}
                        >
                          JPEG
                        </button>
                        <button
                          type="button"
                          className={exportFormat === "png" ? "active" : ""}
                          onClick={() => setExportFormat("png")}
                        >
                          PNG
                        </button>
                        <button
                          type="button"
                          className={exportFormat === "webp" ? "active" : ""}
                          onClick={() => setExportFormat("webp")}
                        >
                          WebP
                        </button>
                      </div>
                    </div>
                    <div className="media-export-group">
                      <label>Taille</label>
                      <select value={exportSize} onChange={(e) => setExportSize(e.target.value)}>
                        {getAvailableSizes(activeMediaUrl).map((size) => (
                          <option key={size.value} value={size.value}>
                            {size.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="media-edit-actions">
                    <button
                      type="button"
                      className="media-secondary"
                      onClick={() => setExportOpen(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="media-primary"
                      onClick={handleExport}
                      disabled={exportLoading}
                    >
                      {exportLoading ? "Export en cours..." : "Exporter"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {editOpen && (
            <div className="media-edit-overlay" onClick={() => setEditOpen(false)}>
              <div className="media-edit" onClick={(event) => event.stopPropagation()}>
                <div className="media-edit-header">
                  <h3>Modifier le media</h3>
                  <button
                    type="button"
                    className="media-edit-close"
                    onClick={() => setEditOpen(false)}
                    aria-label="Fermer"
                  >
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </div>
                <div className="media-edit-body">
                  <div className="media-edit-canvas">
                    <div className="media-edit-image-wrap">
                      <img
                        ref={editImageRef}
                        src={activeMediaUrl}
                        alt="Apercu"
                        onLoad={(event) => {
                          const imgEl = event.currentTarget;
                          const displayW = imgEl.clientWidth;
                          const displayH = imgEl.clientHeight;
                          setEditImageMeta({
                            naturalW: imgEl.naturalWidth || 0,
                            naturalH: imgEl.naturalHeight || 0,
                            displayW,
                            displayH
                          });
                          if (editCropRect.w === 0 || editCropRect.h === 0) {
                            const w = Math.max(displayW * 0.7, 120);
                            const h = Math.max(displayH * 0.7, 120);
                            setEditCropRect({
                              x: Math.max((displayW - w) / 2, 0),
                              y: Math.max((displayH - h) / 2, 0),
                              w,
                              h
                            });
                          }
                        }}
                        style={{
                          transform: `rotate(${editRotate}deg)`,
                          clipPath: editCropApplied && editImageMeta.displayW
                            ? `inset(${(editCropRect.y / editImageMeta.displayH) * 100}% ${((editImageMeta.displayW - editCropRect.x - editCropRect.w) / editImageMeta.displayW) * 100}% ${((editImageMeta.displayH - editCropRect.y - editCropRect.h) / editImageMeta.displayH) * 100}% ${(editCropRect.x / editImageMeta.displayW) * 100}%)`
                            : "none"
                        }}
                      />
                      {editCropMode && (
                        <div
                          className="media-edit-crop"
                          style={{
                            left: editCropRect.x,
                            top: editCropRect.y,
                            width: editCropRect.w,
                            height: editCropRect.h
                          }}
                          onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            cropDragRef.current = {
                              handle: "move",
                              startX: event.clientX,
                              startY: event.clientY,
                              rect: { ...editCropRect }
                            };
                            const onMove = (moveEvent) => {
                              const payload = cropDragRef.current;
                              if (!payload) return;
                              const dx = moveEvent.clientX - payload.startX;
                              const dy = moveEvent.clientY - payload.startY;
                              let x = payload.rect.x + dx;
                              let y = payload.rect.y + dy;
                              const maxX = (editImageMeta.displayW || 0) - payload.rect.w;
                              const maxY = (editImageMeta.displayH || 0) - payload.rect.h;
                              x = Math.max(0, Math.min(x, maxX));
                              y = Math.max(0, Math.min(y, maxY));
                              setEditCropRect({ ...payload.rect, x, y });
                            };
                            const onUp = () => {
                              cropDragRef.current = null;
                              window.removeEventListener("pointermove", onMove);
                              window.removeEventListener("pointerup", onUp);
                            };
                            window.addEventListener("pointermove", onMove);
                            window.addEventListener("pointerup", onUp);
                          }}
                        >
                          {["nw", "ne", "sw", "se"].map((handle) => (
                            <button
                              key={handle}
                              type="button"
                              className={`media-edit-handle ${handle}`}
                              onPointerDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                cropDragRef.current = {
                                  handle,
                                  startX: event.clientX,
                                  startY: event.clientY,
                                  rect: { ...editCropRect }
                                };
                                const onMove = (moveEvent) => {
                                  const payload = cropDragRef.current;
                                  if (!payload) return;
                                  const dx = moveEvent.clientX - payload.startX;
                                  const dy = moveEvent.clientY - payload.startY;
                                  const minSize = 40;
                                  let { x, y, w, h } = payload.rect;
                                  if (payload.handle.includes("n")) {
                                    y = Math.min(Math.max(y + dy, 0), y + h - minSize);
                                    h = Math.max(payload.rect.h - dy, minSize);
                                  }
                                  if (payload.handle.includes("s")) {
                                    h = Math.max(payload.rect.h + dy, minSize);
                                  }
                                  if (payload.handle.includes("w")) {
                                    x = Math.min(Math.max(x + dx, 0), x + w - minSize);
                                    w = Math.max(payload.rect.w - dx, minSize);
                                  }
                                  if (payload.handle.includes("e")) {
                                    w = Math.max(payload.rect.w + dx, minSize);
                                  }
                                  const maxW = editImageMeta.displayW || w;
                                  const maxH = editImageMeta.displayH || h;
                                  w = Math.min(w, maxW - x);
                                  h = Math.min(h, maxH - y);
                                  w = Math.max(w, minSize);
                                  h = Math.max(h, minSize);
                                  setEditCropRect({ x, y, w, h });
                                };
                                const onUp = () => {
                                  cropDragRef.current = null;
                                  window.removeEventListener("pointermove", onMove);
                                  window.removeEventListener("pointerup", onUp);
                                };
                                window.addEventListener("pointermove", onMove);
                                window.addEventListener("pointerup", onUp);
                              }}
                              aria-label="Redimensionner le cadre"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="media-edit-toolbar">
                    <button
                      type="button"
                      className="media-edit-icon"
                      onClick={() => setEditRotate((prev) => (prev - 90 + 360) % 360)}
                      aria-label="Rotation antihoraire"
                    >
                      <span className="material-symbols-rounded">rotate_left</span>
                    </button>
                    <button
                      type="button"
                      className="media-edit-icon"
                      onClick={() => setEditRotate((prev) => (prev + 90) % 360)}
                      aria-label="Rotation horaire"
                    >
                      <span className="material-symbols-rounded">rotate_right</span>
                    </button>
                    <button
                      type="button"
                      className={`media-edit-icon ${editCropMode ? "is-active" : ""}`}
                      onClick={() => setEditCropMode((prev) => !prev)}
                      aria-label="Activer le recadrage"
                    >
                      <span className="material-symbols-rounded">crop</span>
                    </button>
                    <button
                      type="button"
                      className="media-edit-action"
                      onClick={() => {
                        setEditCropApplied(true);
                        setEditCropMode(false);
                      }}
                      disabled={!editCropMode}
                    >
                      Recadrer
                    </button>
                  </div>
                  {editError && <div className="media-error">{editError}</div>}
                  <div className="media-edit-actions">
                    <button
                      type="button"
                      className="media-btn secondary"
                      onClick={() => setEditOpen(false)}
                      disabled={editLoading}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="media-btn"
                      disabled={editLoading}
                      onClick={async () => {
                        if (!detailItem?.mediaItemId || !selectedVersionId) return;
                        const hasTransform = editRotate % 360 !== 0 || editCropApplied;
                        if (!hasTransform) {
                          setEditError("Aucune modification a appliquer.");
                          return;
                        }
                        setEditLoading(true);
                        setEditError("");
                        const body = {
                          media_item_id: detailItem.mediaItemId,
                          media_version_id: selectedVersionId,
                          rotate_deg: editRotate
                        };
                        if (editCropApplied) {
                          const scaleX = editImageMeta.naturalW && editImageMeta.displayW
                            ? editImageMeta.naturalW / editImageMeta.displayW
                            : 1;
                          const scaleY = editImageMeta.naturalH && editImageMeta.displayH
                            ? editImageMeta.naturalH / editImageMeta.displayH
                            : 1;
                          body.crop_x = Math.round(editCropRect.x * scaleX);
                          body.crop_y = Math.round(editCropRect.y * scaleY);
                          body.crop_width = Math.round(editCropRect.w * scaleX);
                          body.crop_height = Math.round(editCropRect.h * scaleY);
                        }
                        try {
                          const res = await fetch("/api/fast/media/transform", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(body)
                          });
                          const data = await res.json();
                          if (!res.ok || !data.ok) {
                            throw new Error(data.error || "Erreur transformation media.");
                          }
                          setEditOpen(false);
                          openDetail(detailItem);
                        } catch (err) {
                          setEditError(err.message || "Erreur transformation media.");
                        } finally {
                          setEditLoading(false);
                        }
                      }}
                    >
                      {editLoading ? "..." : "Valider"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
